import { equals } from 'ramda';
import { useEffect, useRef } from 'react';
import { isDefined } from '../utils';
import { listDifference } from '../utils/list';
import usePrevious from './usePrevious';
export const useSubscribeToEntities = (entities, subscribeFn, unsubscribeFn, streamConnected) => {
    const alreadySubscribed = useRef(false);
    const prevEntities = usePrevious(entities);
    // The purpose of this use effect is to set a boolean guard that will prevent
    // us from re-subscribing as the props update
    useEffect(() => {
        // do nothing at first, we can't mark subscribed until we actually subscribe
        return () => {
            // at the end, set back to false to allow unsubscribe
            alreadySubscribed.current = false;
        };
    }, []);
    // This effect subscribes for each sensor provided, and will handle updates to
    // the sensor list. It prevents subscription churn by using the
    // `alreadySubscribed` ref.
    useEffect(() => {
        if (entities !== undefined &&
            prevEntities !== undefined &&
            !isSameEntityArray(prevEntities, entities)) {
            // If the sensors list has updated for any reason we need to make sure to
            // subscribe to the new sensors
            alreadySubscribed.current = false;
            // Also, need to find any sensors dropped from the new list and
            // unsubscribe from them
            const droppedSensors = listDifference(prevEntities, entities, (item) => item.id);
            droppedSensors.forEach(({ vendor, uuid, id }) => {
                if (vendor) {
                    unsubscribeFn(vendor, uuid, id);
                }
            });
        }
        if (streamConnected && entities !== undefined && entities.length > 0) {
            const unsubscribeFns = [];
            entities.forEach(({ vendor, uuid, id }) => {
                if (uuid && vendor) {
                    if (!alreadySubscribed.current) {
                        subscribeFn(vendor, uuid, id);
                    }
                    unsubscribeFns.push(() => {
                        // alreadySubscribed should always be true from after this loop,
                        // unless the component is being torn down, in which case the first
                        // use effect will have already ran before this clean up and set the
                        // flag back
                        if (!alreadySubscribed.current) {
                            unsubscribeFn(vendor, uuid, id);
                        }
                    });
                }
            });
            alreadySubscribed.current = true;
            return () => unsubscribeFns.forEach((unsubscribeFns) => unsubscribeFns());
        }
    }, [entities, streamConnected, prevEntities, subscribeFn, unsubscribeFn]);
    // Can't just use [] dep array for the effect because we may need to fetch
    // sensors from BE and so sensors array is empty on first render
};
export const isSameEntityArray = (listA, listB) => {
    if (listA.length !== listB.length)
        return false;
    const mappedListA = listA.map((s) => s.id);
    const mappedListB = listB.map((s) => s.id);
    const sortedListA = mappedListA.sort();
    const sortedListB = mappedListB.sort();
    return equals(sortedListA, sortedListB);
};
/**
 *
 * Thermostat Control Event Listener
 *
 * @param thermostat
 * @param useSelectorFn example func below - seemed like best way to handle
 * potential differences in reducer until a more concerted effort is to made to
 * have same reducer
 * () =>
      useSelector(({ subscribedThermostats }: ApplicationState) => {
        if (thermostat) {
          return subscribedThermostats.byId[thermostat.id];
        }
        return undefined;
      }),
 * @param dispatch pass dispatch fn from useDispatch
 * @param subscribeToDeviceReadings raw redux action creator
 * @param unsubscribeFromDeviceReadings raw redux action creator
 * @returns
 */
export const useSubscribeToThermostat = (thermostat, useSelectorFn, dispatch, subscribeToDeviceReadings, unsubscribeFromDeviceReadings) => {
    useDeviceReadingsSubscription(thermostat, dispatch, subscribeToDeviceReadings, unsubscribeFromDeviceReadings);
    const subscribedThermostat = useSelectorFn();
    return subscribedThermostat;
};
export const useSubscribeToRFThermostat = (thermostat, useSelectorFn, dispatch, subscribeToDeviceReadings, unsubscribeFromDeviceReadings) => {
    useDeviceReadingsSubscription(thermostat, dispatch, subscribeToDeviceReadings, unsubscribeFromDeviceReadings);
    const subscribedRFThermostat = useSelectorFn();
    return subscribedRFThermostat;
};
/**
 * ControlBoard Event Listener
 *
 * @param controlBoard
 * @param useSelectorFn example func below - seemed like best way to handle
 * potential differences in reducer until a more concerted effort is to made to
 * have same reducer
 * () =>
 useSelector(({ subscribedControlBoards }: ApplicationState) => {
        if (gateway) {
          return subscribedControlBoards.byId[controlBoard.id];
        }
        return undefined;
      }),
 * @param dispatch pass dispatch fn from useDispatch
 * @param subscribeToDeviceStatus raw redux action creator
 * @param unsubscribeFromDeviceStatus raw redux action creator
 * @returns
 */
export const useSubscribeToControlBoard = (controlBoard, useSelectorFn, dispatch, subscribeToDeviceStatus, unsubscribeFromDeviceStatus) => {
    useEffect(() => {
        let unsubscribe = null;
        if (isDefined(controlBoard)) {
            dispatch(subscribeToDeviceStatus(controlBoard.vendor, controlBoard.uuid, controlBoard.id));
            unsubscribe = () => {
                dispatch(unsubscribeFromDeviceStatus(controlBoard.vendor, controlBoard.uuid, controlBoard.id));
            };
        }
        return () => {
            if (isDefined(unsubscribe)) {
                unsubscribe();
            }
        };
    }, [
        dispatch,
        controlBoard,
        subscribeToDeviceStatus,
        unsubscribeFromDeviceStatus,
    ]);
    const subscribedControlBoard = useSelectorFn();
    return subscribedControlBoard;
};
function useDeviceReadingsSubscription(thermostat, dispatch, subscribeToDeviceReadings, unsubscribeFromDeviceReadings) {
    useEffect(() => {
        let unsubscribe = null;
        if (isDefined(thermostat)) {
            dispatch(subscribeToDeviceReadings(thermostat.vendor, thermostat.uuid, thermostat.id));
            unsubscribe = () => {
                dispatch(unsubscribeFromDeviceReadings(thermostat.vendor, thermostat.uuid, thermostat.id));
            };
        }
        return () => {
            if (isDefined(unsubscribe)) {
                unsubscribe();
            }
        };
    }, [
        dispatch,
        thermostat,
        subscribeToDeviceReadings,
        unsubscribeFromDeviceReadings,
    ]);
}
